/**
 * Spellweave's side of the sprite library: which clip a player's or an enemy's state asks for,
 * and which set draws an enemy (the orc swaps to its bare set). The pure parts every game shares (the art manifest, a set's
 * frames.json, the 8-way direction from an aim, mirroring, clip timing, the recolour plan, the
 * tier for a screen, anchors) come from game-sprites/sets and are re-exported here. No DOM, so
 * the tests run it under node; sprites.ts holds the library that loads and draws.
 */

import { firstClip } from 'game-sprites/sets';
import { waveComposition, type LevelDef } from '@shared/index.js';
import type { ViewEnemy, ViewPlayer } from '../state/viewModel.js';
import type { StatusLook } from './statusFx.js';

export * from 'game-sprites/sets';

/**
 * From this wave on, every set loads: wave 5 is the jotunn's (the first boss wave) and the later
 * enemies follow from wave 6, so they load during wave 4 and its breather.
 */
export const LATE_ART_FROM_WAVE = 4;

/**
 * The enemy sets a level's story sends (its rooms' groups, boiler pushes, the boss and its calls), so the lobby can
 * load them before the act starts instead of each one popping in when its room wakes (2026-10-28). Waves and capture
 * the flag levels have no story: [].
 */
export function levelArt(level: LevelDef | undefined): string[] {
  const story = level?.story;
  if (!story) return [];
  const kinds = new Set<string>();
  const add = (kind: string): void => {
    kinds.add(kind);
    if (kind === 'orc') kinds.add('orc-bare');
  };
  for (const room of story.rooms) {
    for (const g of room.spawn?.groups ?? []) add(g.kind);
    for (const push of room.boiler?.pushes ?? []) for (const g of push) add(g.kind);
    if (room.boss) {
      add(room.boss.kind);
      add(room.boss.calls.kind);
    }
  }
  return [...kinds];
}

/** The sets the page loads at once: the wizard, the arena's pillar and everything the waves before LATE_ART_FROM_WAVE bring. */
export const EARLY_ART: readonly string[] = [...new Set(['wizard', 'pillar',
  ...Array.from({ length: LATE_ART_FROM_WAVE }, (_, i) => waveComposition(i + 1, 1).map((g) => g.kind as string)).flat()])];

/**
 * The spell effects (games-media `bench fx`): shield, rock and ice wall faces, mines, impacts and blasts, storm
 * columns, status overlays, the lava pool, beam strips and flares, spray puffs, the arc strip and projectiles. Small
 * (about 3.5 MB decoded together) and needed from the first cast, so they load with the early art.
 */
export const FX_ART = [
  'fx-shield-wall', 'fx-rock-wall', 'fx-ice-wall', 'fx-mine', 'fx-burst', 'fx-storm', 'fx-status', 'fx-pool',
  'fx-beam', 'fx-spray', 'fx-arc', 'fx-projectile',
] as const;
export type FxSet = (typeof FX_ART)[number];

/** Seconds of knockdown left below which the stand-up plays. */
export const GETUP_S = 0.5;
/** A panicking enemy runs its walk this much faster when the set has no panic clip. */
export const PANIC_RATE = 1.6;

export interface ClipChoice { clip: string; rate: number }

/**
 * The clip a player's state asks for, in priority: dead -> death, knockdown -> knockdown then
 * getup, the act (beam/spray -> channel, cast/arc -> cast, charge, melee -> swing, cast -> cast),
 * moving -> walk, else idle. `has` says which clips the set offers; a missing one falls back
 * along the same order, down to idle.
 */
export function playerClip(p: Pick<ViewPlayer, 'dead' | 'act' | 'vx' | 'vy'>, look: Pick<StatusLook, 'knockdown' | 'knockdownT' | 'anim'>, has: (clip: string) => boolean): ClipChoice {
  const pick = (...names: string[]): string => firstClip(has, names);
  if (p.dead) return { clip: pick('death'), rate: 1 };
  if (look.knockdown) return { clip: look.knockdownT <= GETUP_S ? pick('getup', 'knockdown') : pick('knockdown'), rate: 1 };
  const rate = look.anim;
  switch (p.act) {
    case 'beam': case 'spray': return { clip: pick('channel', 'cast'), rate };
    case 'arc': case 'cast': return { clip: pick('cast', 'channel'), rate };
    case 'charge': return { clip: pick('charge', 'channel'), rate };
    case 'melee': return { clip: pick('swing', 'cast'), rate };
    default: break;
  }
  if (p.vx !== 0 || p.vy !== 0) return { clip: pick('walk'), rate };
  return { clip: 'idle', rate };
}

/**
 * The set that draws an enemy: its kind's, except an orc whose armour is gone, which drops its
 * plates the way the vector orc does (the art library bakes it as `orc-bare`).
 */
export function enemySetName(e: Pick<ViewEnemy, 'k' | 'ar'>): string {
  return e.k === 'orc' && e.ar <= 0 ? 'orc-bare' : e.k;
}

/** The clip an enemy's state asks for: knockdown/getup, then chase -> walk, attack, channel, panic (its own clip or a quick walk), else idle. */
export function enemyClip(e: Pick<ViewEnemy, 'a' | 'vx' | 'vy'>, look: Pick<StatusLook, 'knockdown' | 'knockdownT' | 'anim'>, has: (clip: string) => boolean): ClipChoice {
  const pick = (...names: string[]): string => firstClip(has, names);
  if (look.knockdown) return { clip: look.knockdownT <= GETUP_S ? pick('getup', 'knockdown') : pick('knockdown'), rate: 1 };
  const rate = look.anim;
  switch (e.a) {
    case 'attack': return { clip: pick('attack', 'idle'), rate };
    case 'channel': return { clip: pick('channel', 'attack'), rate };
    case 'panic': return has('panic') ? { clip: 'panic', rate } : { clip: pick('walk'), rate: rate * PANIC_RATE };
    case 'chase': return { clip: pick('walk'), rate };
    default: break;
  }
  if (e.vx !== 0 || e.vy !== 0) return { clip: pick('walk'), rate };
  return { clip: 'idle', rate };
}
